import mapboxgl from "mapbox-gl";
import * as geoService from "./geoService";
import CONSTANTS from "../constants";
import * as utils from "../utils";
import { getSentryClient } from "../errorBoundaryComponent";
import BBOX_DATA from "../bbox_data";

// eslint-disable-next-line import/no-mutable-exports
export let map = null;

export const popup = new mapboxgl.Popup({
  closeButton: false,
  closeOnClick: false,
});

// Array for holding names of poi `partitioned` layers
export const poiPartitionedLayers = Array.from(
  { length: CONSTANTS.POI_PARTITIONED_LAYERS_COUNT },
  (_, index) => `poi_partitioned_${CONSTANTS.POI_PARTITIONED_LAYERS_COUNT}_${index}`,
);
let activePoiFeature = null;

// Used to hold poi id on which cursor is currently hovered
let activeHoveredPoiId = null;

/**
 * Function which is used to set value of `activeHoveredPoiId`
 * @param {*} value Value which needs to be set
 */
export function setActiveHoverPoiId(value) {
  activeHoveredPoiId = value;
}

/**
 * Function which is used to retrieve value of `activeHoveredPoiId`
 */
export function getActiveHoverPoiId() {
  return activeHoveredPoiId;
}

/**
 * Setter for `activePoiFeature`
 */
export function setActivePOIFeature(poiFeature) {
  activePoiFeature = poiFeature;
}

/**
 * Function which is used to return whether current poi selected/hovered is equal to selected poi
 * @param {*} poiId Value of the poiId which needs to be compared against selected poi id
 */
export function IsPoiEqualsActivePoi(poiId) {
  return Boolean(activePoiFeature && poiId === activePoiFeature.id);
}

/**
 * Function which is used to set the pointer on the map canvas
 * @param {*} cursorValue Value of cursor which needs to be set
 */
export function setCursor(cursorValue) {
  map.getCanvas().style.cursor = cursorValue;
}

// Used to hold `fill color` property for state/dma layer
export const STATE_DMA_FILL_COLOR = [
  "step",
  ["coalesce", ["feature-state", "normalizedVisit"], 0],
  CONSTANTS.LAYERS_COLOR_PALETTE.TRANSPARENT_COLOR,
  1,
  CONSTANTS.LAYERS_COLOR_PALETTE.FIRST_COLOR,
  10,
  CONSTANTS.LAYERS_COLOR_PALETTE.SECOND_COLOR,
  20,
  CONSTANTS.LAYERS_COLOR_PALETTE.THIRD_COLOR,
  30,
  CONSTANTS.LAYERS_COLOR_PALETTE.FOURTH_COLOR,
  40,
  CONSTANTS.LAYERS_COLOR_PALETTE.FIFTH_COLOR,
  50,
  CONSTANTS.LAYERS_COLOR_PALETTE.SIXTH_COLOR,
  60,
  CONSTANTS.LAYERS_COLOR_PALETTE.SEVENTH_COLOR,
  70,
  CONSTANTS.LAYERS_COLOR_PALETTE.EIGHTH_COLOR,
  80,
  CONSTANTS.LAYERS_COLOR_PALETTE.NINTH_COLOR,
  90,
  CONSTANTS.LAYERS_COLOR_PALETTE.TENTH_COLOR,
  100,
  CONSTANTS.LAYERS_COLOR_PALETTE.ELEVENTH_COLOR,
];

// Used to hold `fill color` property for geoblocks layer
export const GEOBLOCKS_FILL_COLOR = [
  "step",
  ["coalesce", ["feature-state", "normalizedAffinity"], 0],
  CONSTANTS.LAYERS_COLOR_PALETTE.TRANSPARENT_COLOR,
  0,
  CONSTANTS.LAYERS_COLOR_PALETTE.FIRST_COLOR,
  1,
  CONSTANTS.LAYERS_COLOR_PALETTE.SECOND_COLOR,
  2,
  CONSTANTS.LAYERS_COLOR_PALETTE.THIRD_COLOR,
  3,
  CONSTANTS.LAYERS_COLOR_PALETTE.FOURTH_COLOR,
  4,
  CONSTANTS.LAYERS_COLOR_PALETTE.FIFTH_COLOR,
  5,
  CONSTANTS.LAYERS_COLOR_PALETTE.SIXTH_COLOR,
  6,
  CONSTANTS.LAYERS_COLOR_PALETTE.SEVENTH_COLOR,
  7,
  CONSTANTS.LAYERS_COLOR_PALETTE.EIGHTH_COLOR,
  8,
  CONSTANTS.LAYERS_COLOR_PALETTE.NINTH_COLOR,
  9,
  CONSTANTS.LAYERS_COLOR_PALETTE.TENTH_COLOR,
  10,
  CONSTANTS.LAYERS_COLOR_PALETTE.ELEVENTH_COLOR,
];

/**
 * Function which is used to set `normalizedValue` value of specific layer of specific source
 * @param {*} countGeoIdMapping Array containing mapping of `geo_id` against the count(visits/affinity)
 * @param {*} sourceValue Value of source which needs to be updated
 * @param {*} layer Value of layer whose colors needs to be updated
 * @param {*} mapFeatureKey Value of the attribute for which normalized count needs to be set
 * @param {*} rangeMin Min value of the range in which value needs to be normalized
 * @param {*} rangeMax Max value of the range in which value needs to be normalized
 */
function updateMapWithNormalizedValue(
  countGeoIdMapping,
  sourceValue,
  layer,
  mapFeatureKey = "normalizedVisit",
  rangeMin = 0,
  rangeMax = 100,
) {
  // Finding the boundary values
  const countArray = Object.values(countGeoIdMapping);
  const minCountValue = Math.min.apply(null, countArray);
  const maxCountValue = Math.max.apply(null, countArray);
  // Normalizing each value in countArray
  Object.keys(countGeoIdMapping).forEach((geoId) => {
    let normalizedValue = 0;
    const mapFeatureKeyNormalizedObject = {};
    const actualVisitValue = countGeoIdMapping[geoId];
    // Calling function only if non `0` value is passed
    if (actualVisitValue) {
      normalizedValue = utils.normalizeValue(actualVisitValue, minCountValue, maxCountValue, rangeMin, rangeMax);
      // In case normalized value returned is 0 which is the case when min value is passed
      // So setting the value `1` which is the next minimum for both `state/dma/geoblocks` layers data
      normalizedValue = normalizedValue || 1;
    }
    mapFeatureKeyNormalizedObject[mapFeatureKey] = normalizedValue;
    map.setFeatureState({ source: sourceValue, sourceLayer: layer, id: geoId }, mapFeatureKeyNormalizedObject);
  });
}

/**
 * Method to get Info about Highlighted Feature from the Highlight Layers
 * Iterates over all the Highlight Layers to find out the highlighted feature id
 * @return: First Highlighted Feature, Object{ source: <Sources>, sourceLayer: <Layer>, id: <highlighted feature id> }
 *
 *
 */
function getHighlightedFeatureInfo() {
  // Mapping of Highlight Layer with corresponding source and sourceLayer
  // The ordering is done in order to give preference to DMA Layer
  const layerSourceInfoMaps = [
    {
      layer: CONSTANTS.LAYERS.DMA_HIGHLIGHTED,
      source: CONSTANTS.SOURCES.DMA,
      sourceLayer: CONSTANTS.LAYERS.DMA,
    },
    {
      layer: CONSTANTS.LAYERS.STATE_HIGHLIGHTED,
      source: CONSTANTS.SOURCES.STATE,
      sourceLayer: CONSTANTS.LAYERS.STATE,
    },
  ];
  // eslint-disable-next-line no-restricted-syntax
  for (const layerSourceInfo of layerSourceInfoMaps) {
    const highlightedFeatureID = map.getFilter(layerSourceInfo.layer)[2];
    if (highlightedFeatureID) {
      return {
        source: layerSourceInfo.source,
        sourceLayer: layerSourceInfo.sourceLayer,
        id: highlightedFeatureID,
      };
    }
  }
  return null;
}

/**
 * Function used to reset map to national level in case a `highlighted` or `zoomed in` feature has zero visits
 * @param mapStore: mapStore object which is used to prevent map flying in case `geoType` is `POI`
 *
 */
function useHighlightedOrZoomInFeatureToFlyMap(mapStore) {
  // Preventing flying of map to national level in case `Locations` tab has been selected while data for state/dma is being processed
  if (mapStore.geo && mapStore.geo.geoType === CONSTANTS.GEO_TYPE.POI) {
    return;
  }

  const highlightedOrZoomedInFeatureInfo = getHighlightedFeatureInfo() || getZoomedInFeatureInfo();

  // Checking if no feature is zoomed in or highlighted or is zoomed in or highlighted but doesn't have visits
  if (!highlightedOrZoomedInFeatureInfo || !map.getFeatureState(highlightedOrZoomedInFeatureInfo).visit) {
    // Resetting the state and dma filter
    hideLayers([CONSTANTS.LAYERS.STATE_HIGHLIGHTED, CONSTANTS.LAYERS.DMA_HIGHLIGHTED]);
    // Resetting zoom to national level
    map.flyTo({
      center: CONSTANTS.LAYERS_CONFIG.CENTER,
      zoom: CONSTANTS.LAYERS_CONFIG.DEFAULT_ZOOM,
    });
  }
}

/**
 * Method to get Info about Zoomed In Feature from the Center of the Map
 * Iterates over all the Layers to find out the zoomed in feature id
 * @return: First Zoomed In Feature, Object{ source: <Sources>, sourceLayer: <Layer>, id: <zoomed in feature id> }
 */
function getZoomedInFeatureInfo() {
  // Mapping of Feature Layer with corresponding source and sourceLayer
  // The ordering is done in order to give preference to DMA Layer
  const layerSourceInfoMaps = [
    { layer: CONSTANTS.LAYERS.DMA, source: CONSTANTS.SOURCES.DMA, sourceLayer: CONSTANTS.LAYERS.DMA },
    { layer: CONSTANTS.LAYERS.STATE, source: CONSTANTS.SOURCES.STATE, sourceLayer: CONSTANTS.LAYERS.STATE },
  ];

  // Get centre of map
  const mapCenter = map.project(map.getCenter());
  // eslint-disable-next-line no-restricted-syntax
  for (const layerSourceInfo of layerSourceInfoMaps) {
    const feature = queryRenderedFeature(mapCenter, layerSourceInfo.layer);
    if (feature && feature.properties.id) {
      return {
        source: layerSourceInfo.source,
        sourceLayer: layerSourceInfo.sourceLayer,
        id: feature.properties.id,
      };
    }
  }
  return null;
}

/**
 * Function which is used to pan and zoom map based on provided features geometries
 * @param {*} features Features which are provided for which viewport needs to be adjusted
 * @param {*} padding Value of padding which needs to be passed to `fitBounds` function of the map
 */
function zoomToFeature(features, padding = 50) {
  const inputFeatures = Array.isArray(features) ? features : [features];
  const featureBounds = new mapboxgl.LngLatBounds();
  inputFeatures.forEach((feature) => {
    const { geometry } = feature;
    utils.getBoundsFromGeometryCoordinates(geometry.coordinates, featureBounds);
  });

  if (featureBounds.isEmpty()) {
    console.error("Empty feature bounds received!");
    return;
  }

  map.fitBounds(featureBounds, {
    padding,
    linear: true,
  });
}

/**
 * Zoom and pan the map to given bounding box
 * @param {*} bbox An array containig array of sw and ne latlongs.
 * @param {*} padding Additional padding to maintain between bounding box and map viewport boundaries.
 *
 */
function zoomToBoundingBox(bbox, padding = 50) {
  map.fitBounds(bbox, {
    padding,
    linear: true,
  });
}

/**
 * Function which is used to set filter empty filter on the specified layers
 * @param {*} Layers Value of layers on which filter needs to be set
 */
export function hideLayers(Layers) {
  const inputLayers = Array.isArray(Layers) ? Layers : [Layers];
  inputLayers.map((layer) => map.setFilter(layer, ["in", "id", ""]));
}

/**
 * Function which is used to set `layoutProperty` on map for specific layers
 * @param {*} Layers Value of layers on which filter needs to be set
 * @param {*} visibility Value of layers on which filter needs to be set
 */
export function setLayersVisibility(layers, visibility) {
  const inputLayers = Array.isArray(layers) ? layers : [layers];
  inputLayers.map((layer) => map.setLayoutProperty(layer, "visibility", visibility));
}

/**
 * Query rendered features by using bounding box or filter expression. Only one of
 * bounding box and filter expression is required.
 * @param {*} bbox Bounding box to be queried for rendered features.
 * @param {*} layer Layer(s) which needs to be queried.
 * @param {*} filter Filter expression to find the matching features.
 */
export function queryRenderedFeature(bbox, layer, filter = null) {
  let features = null;

  let layers = layer;

  if (!Array.isArray(layers)) {
    layers = [layers];
  }

  if (filter) {
    features = map.queryRenderedFeatures({ layers, filter });
  } else {
    features = map.queryRenderedFeatures(bbox, { layers });
  }

  if (features && features[0] && features[0].properties) return features[0];

  return null;
}

/**
 * Apply filters based on parameters present in function arguments
 * @param {Number} tenantId Tenant Id of currently logged in user.
 * @param {Number} locationGroupId Currently selected location group id
 * @param {Number} brandId Currently selected brand id
 * @param {String} categoryId Currently selected category id
 * @param {Array<Number>} snapshotPOIIds POI Ids or selected poi id to apply as a filter on poi layer or both layers.
 * @param {Array<Number>} incrementalPOIIds POI Ids to apply as a filter on poi_incremental layer.
 */
export function applyPOIFilters(tenantId, locationGroupId, brandId, categoryId, snapshotPOIIds = [], incrementalPOIIds = []) {
  const multifilter = ["all"];

  if (tenantId) {
    multifilter.push(["in", ["get", "tenant_id"], ["literal", [tenantId, 0]]]);
  }

  /***
   * Since mapbox doesn't support array as feature properties. We are creating comma separated strings with trailing and leading commas.
   * So that every location group id is bounded by commas `,` as used in the filter expression.
   */
  if (locationGroupId) {
    multifilter.push(["in", `,${locationGroupId},`, ["get", "tags"]]);
  }

  if (brandId) {
    multifilter.push(["==", brandId, ["get", "brand_id"]]);
  }

  if (categoryId) {
    multifilter.push(["==", String(categoryId), ["get", "sic_code"]]);
    // Adding this filter to show only those brands which are supported in lip app
    multifilter.push(["==", true, ["get", "brandSupported"]]);
  }

  Object.values(poiPartitionedLayers).map((layer) => map.setFilter(layer, multifilter));

  let poiFilter = snapshotPOIIds;
  let poiIncFilter = incrementalPOIIds;

  if (snapshotPOIIds && !Array.isArray(snapshotPOIIds)) {
    poiFilter = [snapshotPOIIds];
  }

  // now remove poi ids from snapshot which are present in poi incremental layer
  if (poiFilter) {
    poiFilter = poiFilter.filter((poiId) => incrementalPOIIds.indexOf(poiId) === -1);
  }

  // deep cloning the multi filter to be applied on poi_incremental layer
  const poiIncmultiFilter = JSON.parse(JSON.stringify(multifilter));

  if (poiFilter && poiFilter.length) {
    multifilter.push(["in", ["get", "id"], ["literal", poiFilter]]);
  }

  // to handle the scenerios where only selected poi ids is given and we don't have data for both layers,
  // or we don't know in which layer poi is present, we need to apply filters on both layers
  if (snapshotPOIIds && !incrementalPOIIds.length) {
    poiIncFilter = poiFilter;
  }
  if (poiIncFilter && poiIncFilter.length) {
    poiIncmultiFilter.push(["in", ["get", "id"], ["literal", poiIncFilter]]);
  }
  map.setFilter(CONSTANTS.LAYERS.POI, multifilter);
  map.setFilter(CONSTANTS.LAYERS.POI_INCREMENTAL, poiIncmultiFilter);
}

/**
 * Function which is used to set `visits` and `locations` on the `state` or `dma` source provided
 * @param data : Contains the list of objects holding `visits` and `locations` for different geo_ids
 * @param source: It is either between `state` or `dma`
 * @param mapStore: mapStore object which is used to turn off `loader` after sources are updated with features
 */
export function updateSourceVisitsAndLocations(data, source, mapStore) {
  const layerVisitsGeoIdMapping = {};
  const isSourceState = source === CONSTANTS.SOURCES.STATE;
  let sourceLayer;
  let borderLayer = null;
  if (isSourceState) {
    sourceLayer = CONSTANTS.LAYERS.STATE;
    borderLayer = CONSTANTS.LAYERS.STATE_BORDER;
  } else {
    sourceLayer = CONSTANTS.LAYERS.DMA;
    borderLayer = CONSTANTS.LAYERS.DMA_BORDER;
  }
  data.geos.forEach((feature) => {
    const visitCount = feature.visits;
    // Checking if the response contains location count key in `snake_case` otherwise falling back to `camelCase`
    const locationCount = Object.prototype.hasOwnProperty.call(feature, "location_count") ? feature.location_count : feature.locationCount;
    // Checking if the response contains `geo_id` key otherwise falling back to `id`
    const geoId = Object.prototype.hasOwnProperty.call(feature, "geo_id") ? feature.geo_id : feature.id;
    layerVisitsGeoIdMapping[geoId] = visitCount;
    map.setFeatureState({ source, sourceLayer, id: geoId }, { visit: visitCount, location: locationCount });
  });
  if (data.geos && data.geos.length) {
    // Setting the color scheme for provided Layer
    updateMapWithNormalizedValue(layerVisitsGeoIdMapping, sourceLayer, sourceLayer);
    // Checking whether `Locations` tab is not clicked while data for state/dma is being processed
    if (!(mapStore.geo && mapStore.geo.geoType === CONSTANTS.GEO_TYPE.POI)) {
      // Displaying the provided layer after visitation data has been normalized
      setLayersVisibility([sourceLayer, borderLayer], CONSTANTS.LAYOUT_PROPERTIES.VISIBLE);
    }
  }
  // Validating if the data of the state api has loaded and loader is still active on the zoom level related to STATE Layer
  const currentMapZoomlevel = map.getZoom();
  /* eslint no-underscore-dangle: 0 */
  if (
    (isSourceState && mapStore.showLoader && currentMapZoomlevel <= CONSTANTS.LAYERS_CONFIG.STATE_MAX_ZOOM) ||
    (!isSourceState && mapStore.showLoader && currentMapZoomlevel >= CONSTANTS.LAYERS_CONFIG.DMA_MIN_ZOOM)
  ) {
    mapStore.showLoader = false;
  }
  useHighlightedOrZoomInFeatureToFlyMap(mapStore);
}

/**
 * Zoom and Select a DMA
 * @param {*} dmaId Id of the DMA
 */
export function zoomAndSelectDMA(dmaId, mapStore) {
  mapStore.dmaId = dmaId;
  mapStore.stateId = null;
  // Highlight the dma block
  map.setFilter(CONSTANTS.LAYERS.DMA_HIGHLIGHTED, ["in", "id", mapStore.dmaId]);
  // Hide the previously highlighted state block
  hideLayers([CONSTANTS.LAYERS.STATE_HIGHLIGHTED]);
  if (!Object.prototype.hasOwnProperty.call(BBOX_DATA.dmas, dmaId)) {
    console.error(`DMA id: ${dmaId} does not exist in static bounding box data.`);
  }
  const bbox = BBOX_DATA.dmas[dmaId];
  zoomToBoundingBox(bbox, 50);
}

/**
 * Zoom and Select a State
 * @param {*} stateId Id of the State
 */
export function zoomAndSelectState(stateId, mapStore) {
  mapStore.stateId = stateId;
  mapStore.dmaId = null;
  // Highlight the state block
  map.setFilter(CONSTANTS.LAYERS.STATE_HIGHLIGHTED, ["in", "id", mapStore.stateId]);

  // Hide the previous highlighted dma block
  hideLayers([CONSTANTS.LAYERS.DMA_HIGHLIGHTED]);
  if (!Object.prototype.hasOwnProperty.call(BBOX_DATA.states, stateId)) {
    console.error(`State id: ${stateId} does not exist in static bounding box data.`);
  }
  const bbox = BBOX_DATA.states[stateId];
  zoomToBoundingBox(bbox, 50);
}

/**
 * Performs the operations once the `map.once` event is completed for `style.load` event
 *
 *
 */
export function initMap(mapStore) {
  // Initializing the map visits with passed in brand/category
  // Initializing the map visits with location group state/dma distribution
  if (mapStore.stateDistribution) {
    updateSourceVisitsAndLocations(mapStore.stateDistribution, CONSTANTS.SOURCES.STATE, mapStore);
  }
  if (mapStore.dmaDistribution) {
    updateSourceVisitsAndLocations(mapStore.dmaDistribution, CONSTANTS.SOURCES.DMA, mapStore);
  }

  if (mapStore.geo && mapStore.geo.geoType) {
    switch (mapStore.geo.geoType) {
      case CONSTANTS.GEO_TYPE.STATE:
        zoomAndSelectState(mapStore.geo.geoId, mapStore);
        break;
      case CONSTANTS.GEO_TYPE.DMA:
        zoomAndSelectDMA(mapStore.geo.geoId, mapStore);
        break;
      default:
        break;
    }
  }
}

/**
 * Function which is used to show pois based on the values supplied in mapStore
 * @param {*} mapStore Object holding values such as brand,category,locatio group and tenant_id used for filtering
 */
export function showPoisBasedOnFilter(mapStore) {
  const canvas = map.getCanvasContainer().firstElementChild;
  const boundingRect = canvas.getBoundingClientRect();
  const bbox = [
    [0, 0],
    [boundingRect.width, boundingRect.height],
  ];

  // First query features from poi layer
  const poiFeatures = map.queryRenderedFeatures(bbox, { layers: ["poi"] });

  // then query from poi_incremental layer and overwrite features from poi layer
  const poiIncFeatures = map.queryRenderedFeatures(bbox, { layers: ["poi_incremental"] });

  /**
   * Need to reset the layer opacity and filters.
   */
  applyPOIFilters(
    mapStore.tenantId,
    mapStore.locationGroupId,
    mapStore.brandId,
    mapStore.categoryId,
    poiFeatures.map((feature) => feature.id),
    poiIncFeatures.map((feature) => feature.id),
  );
  map.setPaintProperty(CONSTANTS.LAYERS.POI, "icon-opacity", CONSTANTS.POI_LAYER_DEFAULT_COLOR_OPACITY);
  map.setPaintProperty(CONSTANTS.LAYERS.POI_INCREMENTAL, "icon-opacity", CONSTANTS.POI_LAYER_DEFAULT_COLOR_OPACITY);
}

/**
 * Fit the feature bounds of the poi after jump to event has been completed.
 * @param {*} poiId POI ID to fit bounds for
 *
 *
 */
export function fitPOIFeatureBounds(poiId) {
  let poiFeature = queryRenderedFeature(null, CONSTANTS.LAYERS.POI_INCREMENTAL, ["==", "id", poiId]);
  if (!poiFeature) {
    // Seeking feature from POI layer from `snapshot` layer in case it is not found in `incremental`
    poiFeature = queryRenderedFeature(null, CONSTANTS.LAYERS.POI, ["==", "id", poiId]);
    if (!poiFeature) {
      console.error("Unable to find poi feature for poi_id :", poiId);
      return;
    }
    // In case poi is present in default(snapshot)layer, then updating the property of incremental layer
    map.setPaintProperty(CONSTANTS.LAYERS.POI, "icon-opacity", CONSTANTS.POI_LAYER_DEFAULT_COLOR_OPACITY);
    map.setPaintProperty(CONSTANTS.LAYERS.POI_INCREMENTAL, "icon-opacity", 0);
  } else {
    // In case poi is present in incremental layer, then updating the property of default(snapshot) layer
    map.setPaintProperty(CONSTANTS.LAYERS.POI_INCREMENTAL, "icon-opacity", CONSTANTS.POI_LAYER_DEFAULT_COLOR_OPACITY);
    map.setPaintProperty(CONSTANTS.LAYERS.POI, "icon-opacity", 0);
  }
  setActivePOIFeature(poiFeature);

  // Return so that we don't show the polygon
  if (poiFeature.properties.sensitivity === 4) {
    return;
  }
  [
    CONSTANTS.LAYERS.IN_STORE,
    CONSTANTS.LAYERS.IN_STORE_INCREMENTAL,
    CONSTANTS.LAYERS.IN_STORE_BORDER,
    CONSTANTS.LAYERS.IN_STORE_INCREMENTAL_BORDER,
    CONSTANTS.LAYERS.ON_LOT,
    CONSTANTS.LAYERS.ON_LOT_INCREMENTAL,
    CONSTANTS.LAYERS.ON_LOT_BORDER,
    CONSTANTS.LAYERS.ON_LOT_INCREMENTAL_BORDER,
  ].map((layer) => map.setFilter(layer, ["in", "id", poiId]));

  // Looking for features in `incremental` layer first, in case it is not found
  // then look for feature in corresponding snapshot layer
  let instoreFeature = queryRenderedFeature(null, CONSTANTS.LAYERS.IN_STORE_INCREMENTAL, ["==", "id", poiId]);
  if (!instoreFeature) {
    instoreFeature = queryRenderedFeature(null, CONSTANTS.LAYERS.IN_STORE, ["==", "id", poiId]);
    if (instoreFeature) {
      map.setPaintProperty(CONSTANTS.LAYERS.IN_STORE, "fill-opacity", CONSTANTS.LAYERS_CONFIG.IN_STORE.FILL_OPACITY);
      map.setPaintProperty(CONSTANTS.LAYERS.IN_STORE_BORDER, "line-opacity", CONSTANTS.LAYERS_CONFIG.IN_STORE_BORDER.LINE_OPACITY);
      map.setPaintProperty(CONSTANTS.LAYERS.IN_STORE_INCREMENTAL, "fill-opacity", 0);
      map.setPaintProperty(CONSTANTS.LAYERS.IN_STORE_INCREMENTAL_BORDER, "line-opacity", 0);
    }
  } else {
    map.setPaintProperty(CONSTANTS.LAYERS.IN_STORE_INCREMENTAL, "fill-opacity", CONSTANTS.LAYERS_CONFIG.IN_STORE.FILL_OPACITY);
    map.setPaintProperty(
      CONSTANTS.LAYERS.IN_STORE_INCREMENTAL_BORDER,
      "line-opacity",
      CONSTANTS.LAYERS_CONFIG.IN_STORE_BORDER.LINE_OPACITY,
    );
    map.setPaintProperty(CONSTANTS.LAYERS.IN_STORE, "fill-opacity", 0);
    map.setPaintProperty(CONSTANTS.LAYERS.IN_STORE_BORDER, "line-opacity", 0);
  }

  let onlotFeature = queryRenderedFeature(null, CONSTANTS.LAYERS.ON_LOT_INCREMENTAL, ["==", "id", poiId]);
  if (!onlotFeature) {
    onlotFeature = queryRenderedFeature(null, CONSTANTS.LAYERS.ON_LOT, ["==", "id", poiId]);
    if (onlotFeature) {
      map.setPaintProperty(CONSTANTS.LAYERS.ON_LOT, "fill-opacity", CONSTANTS.LAYERS_CONFIG.ON_LOT.FILL_OPACITY);
      map.setPaintProperty(CONSTANTS.LAYERS.ON_LOT_BORDER, "line-opacity", CONSTANTS.LAYERS_CONFIG.ON_LOT_BORDER.LINE_OPACITY);
      map.setPaintProperty(CONSTANTS.LAYERS.ON_LOT_INCREMENTAL, "fill-opacity", 0);
      map.setPaintProperty(CONSTANTS.LAYERS.ON_LOT_INCREMENTAL_BORDER, "line-opacity", 0);
    }
  } else {
    map.setPaintProperty(CONSTANTS.LAYERS.ON_LOT_INCREMENTAL, "fill-opacity", CONSTANTS.LAYERS_CONFIG.ON_LOT.FILL_OPACITY);
    map.setPaintProperty(CONSTANTS.LAYERS.ON_LOT_INCREMENTAL_BORDER, "line-opacity", CONSTANTS.LAYERS_CONFIG.ON_LOT_BORDER.LINE_OPACITY);
    map.setPaintProperty(CONSTANTS.LAYERS.ON_LOT, "fill-opacity", 0);
    map.setPaintProperty(CONSTANTS.LAYERS.ON_LOT_BORDER, "line-opacity", 0);
  }

  // Checking in case either inStore/onlot Feature is present
  if (instoreFeature || onlotFeature) {
    if (!onlotFeature) {
      zoomToFeature(instoreFeature, 100);
    } else {
      zoomToFeature(onlotFeature, 100);
    }
  }
}

/**
 * Loads the GeoBlocks data for a POI and store it into the feature state.
 * @param {*} poiTradeAreaToggleControl CustomToggleControl instance for disabling the button
 */
export function loadGeoblockData(poiTradeAreaToggleControl, authToken) {
  // Checking whether `activePoiFeature` exists, before accessing the properties
  if (!activePoiFeature) {
    return;
  }
  const activePoiId = activePoiFeature.id;
  geoService
    .fetchPoiGeoBlocksAffinityData(activePoiId, authToken)
    .then((res) => res.json())
    .then((data) => {
      if (!Object.prototype.hasOwnProperty.call(data, activePoiId) || !data[activePoiId].geoblock_affinity) {
        console.warn("No geoblocks are associated with this poi id");
        return;
      }
      poiTradeAreaToggleControl.setItemState(CONSTANTS.MAP_TYPE.TRADE_AREA, false);
      map.setFeatureState(activePoiFeature, {
        geoblocksData: data,
      });
    })
    .catch((error) => {
      getSentryClient().captureException(error);
    });
}

/**
 * Method which is used to generate Geo Blocks when `Trade Area` button is clicked
 * @param {*} lat Latitude value of poi for which Geo Blocks needs to be generated
 * @param {*} lon Longitude value of poi for which Geo Blocks needs to be generated
 *
 */
export function generateGeoBlocks(lat, lon) {
  if (!activePoiFeature) {
    console.warn("No active Poi Feature is present");
    return;
  }

  let geoBlocksFilter = ["in", "id"];
  const filteredIds = [];
  const affinityGeoBlockMapping = {};

  const featureState = map.getFeatureState(activePoiFeature);

  const data = featureState.geoblocksData;

  if (!data) {
    console.error("Missing geoblocks data from poi with enabled geoblocks button.");
  }

  const geoblocksAffinity = data[activePoiFeature.id].geoblock_affinity;
  geoblocksAffinity.split(",").forEach((value) => {
    let [geoBlockId, affinity] = value.split(":");
    geoBlockId = parseInt(geoBlockId, 10);
    map.setFeatureState({ source: CONSTANTS.LAYERS.GEO_BLOCKS, sourceLayer: CONSTANTS.LAYERS.GEO_BLOCKS, id: geoBlockId }, { affinity });
    affinityGeoBlockMapping[geoBlockId] = affinity;
    filteredIds.push(geoBlockId);
  });

  updateMapWithNormalizedValue(
    affinityGeoBlockMapping,
    CONSTANTS.SOURCES.GEO_BLOCKS,
    CONSTANTS.LAYERS.GEO_BLOCKS,
    "normalizedAffinity",
    0,
    10,
  );

  geoBlocksFilter = geoBlocksFilter.concat(filteredIds);
  // Displaying GeoBlocks associated with POI
  map.setFilter(CONSTANTS.LAYERS.GEO_BLOCKS, geoBlocksFilter);
  map.setFilter(CONSTANTS.LAYERS.GEO_BLOCKS_BORDER, geoBlocksFilter);
  map.flyTo(
    {
      center: [lon, lat],
      zoom: CONSTANTS.LAYERS_CONFIG.GEO_BLOCK_ZOOM,
    },
    {
      name: CONSTANTS.EVENTS.SHOW_GEOBLOCKS,
      filter: geoBlocksFilter,
    },
  );
}

/**
 * Take the necessary action, when currently selected poi is changed.
 * @param {*} poiId Currently selected poi, after change.
 * @param {*} lat latitude of the poi.
 * @param {*} lon longitude of the poi.
 *
 */
export function handleActivePOIChange(poiId, lat, lon) {
  /**
   * Set filter on poi features to start rendering them on map
   */
  [CONSTANTS.LAYERS.POI, CONSTANTS.LAYERS.POI_INCREMENTAL].map((layer) =>
    map.setLayoutProperty(layer, "icon-image", ["case", ["==", ["get", "id"], poiId], "circle", "custom-marker"]),
  );
  map.jumpTo(
    {
      center: [lon, lat],
      zoom: CONSTANTS.LAYERS_CONFIG.POI_ZOOM,
    },
    {
      name: CONSTANTS.EVENTS.POI_SELECTED,
      poiId,
    },
  );
}

/**
 * Function which is used to display popup over poi feature
 * @param {*} coordinates Value of lat/lng where popup needs to be shown
 * @param {*} name Name of the poi to be shown in the popup
 * @param {*} address Address of the poi to be shown in the popup
 */
export function displayPoiPopup(coordinates, name, address) {
  popup.setLngLat(coordinates).setHTML(`<strong>${name}</strong><br>${address}`).addTo(map);
}

/**
 * Function which is used to update the `name` and `address` for the feature(poi id) passed for specific layer + display popup on poi
 * @param {*} feature Value of the feature containing poi id whose state needs to be updated
 * @param {*} layer Value of layer on which feature `state` object(custom user defined key value pair) needs to be updated
 * @param {*} authToken UL authentication token
 */
export function updatePoiLayerFeature(feature, layer, authToken) {
  // Checking if feature already contains `name` and `address`, then skipping the api call
  const featureState = feature.state;
  const featureCoordinatesValue = [...feature.geometry.coordinates];
  // Checking for multi point for the cases where more than one poi is present at the same lat/lng across different zoom levels
  // In that case, picking up the first poi
  const coordinates = feature.geometry.type === "MultiPoint" ? featureCoordinatesValue[0] : featureCoordinatesValue;

  if (
    Object.keys(featureState).length &&
    Object.prototype.hasOwnProperty.call(featureState, "name") &&
    Object.prototype.hasOwnProperty.call(featureState, "address")
  ) {
    displayPoiPopup(coordinates, featureState.name, featureState.address);
    return;
  }
  const poiId = feature.properties.id;
  const source = layer === CONSTANTS.LAYERS.POI ? CONSTANTS.SOURCES.POI : CONSTANTS.SOURCES.POI_INCREMENTAL;
  geoService
    .fetchPoiNameAndAddress([poiId], authToken)
    .then((res) => res.json())
    .then((data) => {
      if (Object.keys(data).length) {
        Object.keys(data).forEach(function processApiResponse(poi) {
          // Checking that `poi` doesn't contain null in the response
          if (!data[poi]) {
            console.error("Name and address information not found for the following poi id", poi);
            return;
          }
          const { name, queried_form: address } = data[poi];
          map.setFeatureState({ source, sourceLayer: layer, id: poiId }, { name, address });
          // Display popup only if cursor is still present on that specific poi which initiated the api call
          if (activeHoveredPoiId === poiId) {
            displayPoiPopup(coordinates, name, address);
          }
        });
      }
    });
}

/**
 * Method which is responsible for computing bounds of Geo Blocks Features
 * @param {*} geoBlocksFilter Filter which is used to find the associated features in Geo Blocks Layers
 *
 */
export function computeGeoBlocksBounds(geoBlocksFilter) {
  const geoBlockFeatures = map.queryRenderedFeatures({ layers: [CONSTANTS.LAYERS.GEO_BLOCKS], filter: geoBlocksFilter });
  // Setting the bounds according to the features
  zoomToFeature(geoBlockFeatures, 0);
}

/**
 * Function which is used to display popup when mouse is moved over state/dma layer
 * @param {*} point Point on the map at which mouse is moved
 * @param {*} lngLat Position on the map where popup needs to be displayed
 * @param {*} layer Layer for which message neeeds to be generated
 *
 *
 */
export function displayPopup(point, lngLat, layer) {
  let message = null;
  const feature = map.queryRenderedFeatures(point, { layers: [layer] });
  if (feature && feature.length === 1) {
    message = utils.generatePopUpMessage(feature[0], layer);
  }
  if (message) {
    popup.setLngLat(lngLat).setHTML(message).addTo(map);
  }
}

/**
 * Method which is used to focus map on currently selected POI and also hide any
 * Geo Blocks associated with that poi
 * @param {*} lat Latitude value at which map needs to be centered
 * @param {*} lon Longitude value at which map needs to be centered
 *
 *
 */
export function switchToPOI(lat, lon) {
  // Turning off the visibility of Geo block layers
  map.jumpTo({
    center: [lon, lat],
    zoom: CONSTANTS.LAYERS_CONFIG.POI_ZOOM,
  });
}

/**
 * Function used to restore map state, every time poi toggle control's value changes, before rendering the new state changes.
 * @param {*} mapStore
 */
export function resetPOIToggleControl(mapStore) {
  hideLayers([CONSTANTS.LAYERS.GEO_BLOCKS, CONSTANTS.LAYERS.GEO_BLOCKS_BORDER]);
  const selectedPOIId = activePoiFeature ? activePoiFeature.id : null;
  applyPOIFilters(mapStore.tenantId, mapStore.locationGroupId, mapStore.brandId, mapStore.categoryId, selectedPOIId);
}

/**
 * Setter function for map instance, as we can't set the exported members from outside.
 * @param {*} mapInstance
 */
export function setMap(mapInstance) {
  map = mapInstance;
}

/**
 * Function which is used to set the padding of the map with the values provided
 * @param {*} paddingObject
 */
export function setMapPadding(paddingObject) {
  // Check in case object is empty
  if (Object.keys(paddingObject).length === 0) {
    return;
  }
  const padding = JSON.parse(JSON.stringify(paddingObject));
  // Updating the spinner position based on padding provided
  utils.setSpinnerPosition(padding);
  // Using idle to prevent cancellation of `flyTo` in certain cases
  map.once("idle", () => map.easeTo({ padding }));
}
